import React from 'react';
import './InputLine.css';
import _ from 'lodash';

import {
    AbstractHorizontalContainer,
    ChordContainer,
    HorizontalNoteContainer,
} from '../model/containers';

import { RollRedrawObserver } from '../utils/RollRedrawObserver';

import { CaretController } from '../utils/ScrollController';
import ChordRoll from './rolls/ChordRoll';
import PianoRoll from './rolls/PianoRoll';
import {
    RydProject
} from '../model/project';
import { RydTimelineHeader } from './widgets/rolls/RydTimelineHeader';
import {
    useSetWorkspaceInputSourceAssignments,
    useSetWorkspaceInstrumentAssignments,
    useSetWorkspaceLines,
    useSetWorkspaceTracks,
    useSetWorkspaceUIState,
    useWorkspaceComputedContextFrame,
    useWorkspaceInputSourceAssignments,
    useWorkspaceInstrumentAssignments,
    useWorkspaceLines,
    useWorkspaceTracks,
    useWorkspaceUIState
} from '../stores/WorkspaceStore';
import { useMixerStore } from '../stores/MixerStore';
import { RydContextPlane } from './contextPlane/RydContextPlane';


function getAndAssertSingularChordContainer(
    tracks: (AbstractHorizontalContainer<any>)[]
) : [ChordContainer | null, number] {
    // FIXME: we assume there's only one
    const index = tracks.findIndex(t => t instanceof ChordContainer);
    return [index > -1 ? tracks[index] as ChordContainer : null, index];
}

export function InputLineContainer({
    project,
    redrawObserver,
    caretController
}: {
    project: RydProject,
    redrawObserver?: RollRedrawObserver,
    caretController?: CaretController
}) {
    const instrumentAssignments = useWorkspaceInstrumentAssignments();
    const setWorkspaceInstrumentAssignments = useSetWorkspaceInstrumentAssignments();
    const sourceAssignments = useWorkspaceInputSourceAssignments();
    const setSourceAssignments = useSetWorkspaceInputSourceAssignments();
    const workspaceUiState = useWorkspaceUIState();
    const setWorkspaceUIState = useSetWorkspaceUIState();
    const computedContextFrame = useWorkspaceComputedContextFrame();
    const setWorkspaceTracks = useSetWorkspaceTracks();
    const workspaceTracks = useWorkspaceTracks();
    const projectLines = useWorkspaceLines();
    const setProjectLines = useSetWorkspaceLines();
    const mixerStore = useMixerStore();

    const [harmonyTrack, harmonyTrackIndex] = getAndAssertSingularChordContainer(projectLines);

    return (
        <>
            <RydContextPlane
                project={project}
                caretController={caretController}
            />
            <RydTimelineHeader
                project={project}
                caretController={caretController}
            />
            {harmonyTrack &&
                <ChordRoll
                    trackId={harmonyTrackIndex}
                    line={harmonyTrack}
                    contextFrame={computedContextFrame}
                    caretController={caretController}
                    redrawObserver={redrawObserver}
                    mode='track'
                    trackUiState={workspaceUiState.tracks.get(harmonyTrackIndex)!}
                    setTrackUIState={(uiState) => {
                        project.uiState.tracks.set(harmonyTrackIndex, uiState);
                        setWorkspaceUIState({...project.uiState});
                    }}
                    updateLineCB={(hnc: ChordContainer) => {
                        projectLines[harmonyTrackIndex] = hnc;
                        setProjectLines([...projectLines]);
                        project.put(harmonyTrackIndex, hnc);
                    }}
                />}
            {projectLines.map((container, i) => {
                // harmonyTrackIndex is on the same index space as containers,
                // thus we MUST keep the indexes of the array stable.
                const currentTrack = workspaceTracks.get(i);
                if (!currentTrack) return (<div key={i}>Error: no track on {i}</div>);
                if (!(container instanceof HorizontalNoteContainer)) return null;
                return <PianoRoll
                    project={project}
                    key={i}
                    mode='track'
                    track={currentTrack}
                    setTrack={(track) => {
                        project.tracks.set(i, {...track});
                        setWorkspaceTracks(new Map(project.tracks));
                    }}
                    hnc={container as HorizontalNoteContainer}
                    uiState={workspaceUiState.tracks.get(i)}
                    setTrackUIState={(uiState) => {
                        project.uiState.tracks.set(uiState.trackId, uiState);
                        setWorkspaceUIState({...project.uiState});
                    }}
                    contextFrame={computedContextFrame}
                    readonly={false}
                    caretController={caretController}
                    redrawObserver={redrawObserver}
                    trackInstrument={instrumentAssignments.get(i)}
                    setTrackInstrument={(instrument) => {
                        // FIXME: should we have these default values?
                        const t = project.mixerState.tracks.get(i) || {
                            id: i,
                            name: `track-${i}`,
                            gain: 1.0,
                            pan: 0.0,
                            muted: false,
                            solo: false,
                            onBus: true
                        };

                        project.instrumentAssignments.set(i, instrument);
                        project.mixerState.tracks.set(i, {
                            ...t!,
                            sourceNode: instrument.getOutputNode()
                        });
                        mixerStore.mixer?.removeTrack(i);
                        mixerStore.mixer?.addTrack({
                            id: t.id,
                            name: t.name,
                            gain: t.gain,
                            pan: t.pan,
                            muted: t.muted,
                            solo: t.solo,
                            sourceNode: instrument.getOutputNode(),
                            onBus: t.onBus
                        });
                        setWorkspaceInstrumentAssignments(new Map(project.instrumentAssignments));
                        mixerStore.forceUpdate();
                    }}

                    inputSource={sourceAssignments.get(i)}
                    setInputSource={(source) => {
                        sourceAssignments.set(i, source);
                        setSourceAssignments(new Map(sourceAssignments));
                    }}
                />
            })}
        </>
    );
}

export default InputLineContainer;